/**
 * The project groups the settings scope menus offer.
 *
 * Settings uses the same logical projects the sidebar groups by: one row per
 * canonical key, named by its first member, spanning every environment a
 * checkout lands on. Adapted from upstream's `useSettingsProjectGroups`
 * (pingdotgg/t3code, MIT, pin e3e7cc3f), which builds the same shape from its
 * sidebar snapshots; forge derives the key with its own `deriveLogicalProjectKey`
 * so the scope can never disagree with the sidebar about what one project is.
 *
 * @module
 */

import { deriveLogicalProjectKey } from "../../logicalProject";
import { resolveProjectGroupingMode, type ProjectGroupingOverrides } from "../../projectGrouping";
import type { Project } from "../../types";

import type { SettingsScopeGroup } from "./settingsScope";

export function buildSettingsScopeGroups(
  projects: readonly Project[],
  overrides: ProjectGroupingOverrides,
): readonly SettingsScopeGroup[] {
  const byKey = new Map<string, { displayName: string; environmentIds: Set<string> }>();
  for (const project of projects) {
    const projectKey = deriveLogicalProjectKey(
      project,
      resolveProjectGroupingMode(overrides, project),
    );
    let entry = byKey.get(projectKey);
    if (!entry) {
      entry = { displayName: project.name, environmentIds: new Set() };
      byKey.set(projectKey, entry);
    }
    entry.environmentIds.add(project.environmentId);
  }
  return [...byKey.entries()]
    .map(([projectKey, entry]) => ({
      projectKey,
      displayName: entry.displayName,
      environmentIds: [...entry.environmentIds],
    }))
    .toSorted((left, right) => left.displayName.localeCompare(right.displayName));
}
